// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Wrench, Plus, Search, AlertTriangle } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, Select, Pagination, DataTable, Skeleton, Input } from '@/components/ui'
import { MobileSortControl } from '@/components/ui/MobileSortControl'
import type { Column } from '@/components/ui/DataTable'
import { useEquipment, type EquipmentFilters } from '@/hooks/useCmms'
import { useProperties } from '@/hooks/useProperties'
import { useSelectedPropertyStore } from '@/stores'
import { useListSort } from '@/hooks/useListSort'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import type { Equipment } from '@/types'

export function EquipmentListPage() {
  const { t } = useTranslation('cmms')
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()
  const formatDate = useDateFormatter()
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<EquipmentFilters>({})
  const [searchQuery, setSearchQuery] = useState('')
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  // Property scope = the single global top-right picker (header switcher on
  // desktop, PageHeader titleAction on mobile) — consistent with the other list
  // pages; not a page-local filter.
  const selectedPropertyId = useSelectedPropertyStore((s) => s.selectedPropertyId)
  const setSelectedPropertyId = useSelectedPropertyStore((s) => s.setSelectedPropertyId)

  // EP-97: default-rule page — updated_at desc, user-selectable server-side sort.
  const { sort, order, setSort, toggleOrder, sortParams } = useListSort({
    defaultSort: 'updated_at',
    defaultOrder: 'desc',
  })
  const handleSort = (key: string) => { setPage(1); setSort(key) }
  const handleToggleOrder = () => { setPage(1); toggleOrder() }

  const { data, isLoading, isFetching, error } = useEquipment(page, { ...filters, property_id: selectedPropertyId ?? undefined, q: searchQuery || undefined, ...sortParams })
  const { data: propertiesData } = useProperties()

  const equipment = data?.data || []
  const properties = propertiesData?.data || []

  const propertyOptions = [
    { value: '', label: t('filters.all_properties') },
    ...properties.map((p) => ({ value: String(p.id), label: p.name })),
  ]

  const categoryOptions = [
    { value: '', label: t('filters.all_categories') },
    ...(['hvac', 'plumbing', 'electrical', 'elevator', 'fire_safety', 'security', 'general'] as const).map(
      (c) => ({ value: c, label: t(`categories.${c}`) })
    ),
  ]

  const statusOptions = [
    { value: '', label: t('filters.all_statuses') },
    ...(['operational', 'needs_repair', 'out_of_service', 'decommissioned'] as const).map(
      (s) => ({ value: s, label: t(`equipment_status.${s}`) })
    ),
  ]

  const getStatusBadge = (item: Equipment) => {
    const variants: Record<Equipment['status'], 'success' | 'warning' | 'error' | 'ghost'> = {
      operational: 'success',
      needs_repair: 'warning',
      out_of_service: 'error',
      decommissioned: 'ghost',
    }
    return <Badge variant={variants[item.status]}>{t(`equipment_status.${item.status}`)}</Badge>
  }

  const isWarrantyExpiringSoon = (item: Equipment) => {
    if (!item.warranty_expires_at) return false
    const daysUntilExpiry = Math.floor(
      (new Date(item.warranty_expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
    )
    return daysUntilExpiry >= 0 && daysUntilExpiry <= 30
  }

  const columns: Column<Equipment>[] = [
    {
      key: 'name',
      header: t('labels.name'),
      sortable: true,
      render: (item) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{item.name}</span>
          {isWarrantyExpiringSoon(item) && (
            <AlertTriangle className="w-4 h-4 text-warning" />
          )}
        </div>
      ),
    },
    { key: 'category', header: t('labels.category'), render: (item) => t(`categories.${item.category}`), hideBelow: 'md' },
    { key: 'status', header: t('labels.status'), sortable: true, render: (item) => getStatusBadge(item) },
    { key: 'location', header: t('labels.location'), render: (item) => item.location || '-', hideBelow: 'md' },
    { key: 'property', header: t('labels.property'), render: (item) => item.property?.name || '-', hideBelow: 'lg' },
    {
      key: 'warranty_expires_at',
      header: t('labels.warranty_expires_at'),
      sortable: true,
      render: (item) =>
        item.warranty_expires_at ? formatDate(item.warranty_expires_at) : '-',
      hideBelow: 'lg',
    },
  ]

  // EP-97: mobile sort options mirror the allow-listed sortable columns. Labels
  // reuse the column-header translations; the default updated_at gets a "Recently
  // updated" label since it has no visible column.
  const sortOptions = [
    { value: 'updated_at', label: tc('sort.by_last_updated') },
    { value: 'name', label: t('labels.name') },
    { value: 'status', label: t('labels.status') },
    { value: 'warranty_expires_at', label: t('labels.warranty_expires_at') },
  ]

  const activeFilterCount = [filters.category || '', filters.status || ''].filter(Boolean).length

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: t('equipment.title') }]} />

      <PageHeader
        title={t('equipment.title')}
        description={t('equipment.description')}
        isRefreshing={isFetching && !isLoading}
        // Property scope = the single top-right picker (header switcher on
        // desktop, this titleAction on mobile) — reads the global store, no
        // in-body picker. Consistent with the other list pages.
        titleAction={
          <Select
            data-testid="page-property-picker-mobile"
            aria-label={tc('labels.select_property')}
            size="sm"
            value={selectedPropertyId ? String(selectedPropertyId) : ''}
            onChange={(e) => {
              setSelectedPropertyId(e.target.value ? Number(e.target.value) : null)
              setPage(1)
            }}
            options={propertyOptions}
          />
        }
        primaryAction={{
          key: 'new',
          label: t('equipment.new'),
          icon: Plus,
          to: '/equipment/new',
        }}
      />

      <Card>
        {/* Search — always visible */}
        <div className="w-full min-w-0">
          <Input
            label={tc('actions.search')}
            leftIcon={<Search className="w-4 h-4" />}
            placeholder={t('filters.search_equipment')}
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1) }}
          />
        </div>

        {/* Mobile-only toggle */}
        <button
          type="button"
          className="sm:hidden flex items-center justify-between w-full cursor-pointer select-none font-medium mt-3"
          aria-expanded={mobileFiltersOpen}
          aria-controls="equipment-filters-body"
          onClick={() => setMobileFiltersOpen((v) => !v)}
        >
          <span>
            {tc('filters.toggle')}
            {activeFilterCount > 0 && (
              <Badge variant="count" size="sm" className="ml-2">{activeFilterCount}</Badge>
            )}
          </span>
          <span className={`text-base-content/60 transition-transform duration-200 ${mobileFiltersOpen ? 'rotate-180' : ''}`}>▾</span>
        </button>

        <div
          id="equipment-filters-body"
          className={`flex-wrap gap-4 items-end mt-3 ${mobileFiltersOpen ? 'flex' : 'hidden sm:flex'}`}
        >
          {/* EP-97: mobile sort control — desktop sorts via clickable table headers. */}
          <div className="w-full sm:hidden">
            <MobileSortControl
              options={sortOptions}
              value={sort}
              order={order}
              onChange={handleSort}
              onToggleOrder={handleToggleOrder}
            />
          </div>
          <Select
            label={t('labels.category')}
            value={filters.category || ''}
            onChange={(e) => {
              setFilters((f) => ({ ...f, category: e.target.value || undefined }))
              setPage(1)
            }}
            options={categoryOptions}
            className="w-full sm:w-48 min-w-0"
          />
          <Select
            label={t('labels.status')}
            value={filters.status || ''}
            onChange={(e) => {
              setFilters((f) => ({ ...f, status: e.target.value || undefined }))
              setPage(1)
            }}
            options={statusOptions}
            className="w-full sm:w-48 min-w-0"
          />
        </div>
      </Card>

      {isLoading ? (
        <Card>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        </Card>
      ) : error ? (
        <Card>
          <EmptyState
            title={t('messages.error_loading')}
            description={t('messages.error_description')}
            action={<Button onClick={() => window.location.reload()}>{tc('actions.retry')}</Button>}
          />
        </Card>
      ) : !equipment.length ? (
        <Card>
          <EmptyState
            icon={<Wrench className="w-12 h-12" />}
            title={t('empty.equipment')}
            action={
              <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => navigate('/equipment/new')}>
                {t('equipment.new')}
              </Button>
            }
          />
        </Card>
      ) : (
        <Card>
          <DataTable
            columns={columns}
            data={equipment}
            keyExtractor={(item) => item.id}
            onRowClick={(item) => navigate(`/equipment/${item.id}`)}
            sortBy={sort}
            sortOrder={order}
            onSort={handleSort}
          />
        </Card>
      )}

      {data?.meta && data.meta.total_count > 0 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={data.meta.current_page}
            totalPages={data.meta.total_pages}
            totalItems={data.meta.total_count}
            pageSize={data.meta.per_page}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  )
}
